import React, { createContext, useContext, useMemo, useState } from 'react';
import { useEffect } from 'react';

const ONBOARDING_CONTEXT_KEY = 'myapartment_onboarding_context_v1';
const ONBOARDING_FLOW_KEY = 'myapartment_onboarding_flow_v1';

export type SharedOnboardingContext = {
  step: string;
  progress: number;
  stepLabel: string;
  stepPrompt: string;
  updatedAt?: number;
};

export type StepSelectionRequest = {
  step?: string;
  blockCount?: string;
  floors?: number;
  unitsPerFloor?: number;
  openSummary?: boolean;
  ts?: number;
};

type OnboardingFlowContextValue = {
  onboardingCtx: SharedOnboardingContext | null;
  setOnboardingCtx: React.Dispatch<React.SetStateAction<SharedOnboardingContext | null>>;
  flowData: Record<string, unknown>;
  setFlowData: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  stepSelectionRequest: StepSelectionRequest | null;
  setStepSelectionRequest: React.Dispatch<React.SetStateAction<StepSelectionRequest | null>>;
};

const OnboardingFlowContext = createContext<OnboardingFlowContextValue | undefined>(undefined);

export const OnboardingFlowProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [onboardingCtx, setOnboardingCtx] = useState<SharedOnboardingContext | null>(() => {
    try {
      const raw = localStorage.getItem(ONBOARDING_CONTEXT_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as SharedOnboardingContext;
      if (!parsed?.step || typeof parsed.progress !== 'number') return null;
      return parsed;
    } catch {
      return null;
    }
  });
  const [flowData, setFlowData] = useState<Record<string, unknown>>(() => {
    try {
      const raw = localStorage.getItem(ONBOARDING_FLOW_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as unknown;
      if (!parsed || typeof parsed !== 'object') return {};
      return parsed as Record<string, unknown>;
    } catch {
      return {};
    }
  });
  const [stepSelectionRequest, setStepSelectionRequest] = useState<StepSelectionRequest | null>(null);

  const value = useMemo(
    () => ({
      onboardingCtx,
      setOnboardingCtx,
      flowData,
      setFlowData,
      stepSelectionRequest,
      setStepSelectionRequest,
    }),
    [onboardingCtx, flowData, stepSelectionRequest]
  );

  useEffect(() => {
    try {
      if (onboardingCtx) {
        localStorage.setItem(ONBOARDING_CONTEXT_KEY, JSON.stringify(onboardingCtx));
      }
    } catch {
      // ignore
    }
  }, [onboardingCtx]);

  useEffect(() => {
    try {
      localStorage.setItem(ONBOARDING_FLOW_KEY, JSON.stringify(flowData));
    } catch {
      // ignore
    }
  }, [flowData]);

  return (
    <OnboardingFlowContext.Provider value={value}>
      {children}
    </OnboardingFlowContext.Provider>
  );
};

export function useOnboardingFlow() {
  const ctx = useContext(OnboardingFlowContext);
  if (!ctx) {
    throw new Error('useOnboardingFlow must be used within OnboardingFlowProvider');
  }
  return ctx;
}
